import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';
import { Logo } from '../components/ui/logo.js';
import { useLogin, useMe } from '../hooks';
import { zodResolver } from '../lib/zod-resolver.js';
import { type LoginFormValues, loginSchema } from '../validations/login.js';

export default function Login() {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const location = useLocation();
  type LocationState = {
    from?: {
      pathname?: string;
    };
  };
  const previousLocation = location.state as LocationState | null;
  const from = previousLocation?.from?.pathname ?? '/devices';

  useEffect(() => {
    if (me) navigate(from, { replace: true });
  }, [me, navigate, from]);

  const {
    register,
    handleSubmit,
    setFocus,
    reset,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  useEffect(() => {
    setFocus('username');
  }, [setFocus]);

  const { mutate: login, isPending, error } = useLogin();

  const onSubmit = (values: LoginFormValues) => {
    login(
      { username: values.username.trim(), password: values.password },
      {
        onSuccess: () => {
          reset();
          navigate(from, { replace: true });
        },
      },
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm bg-card shadow rounded-xl p-6">
        <Logo variant="stacked" size="xl" accent className="flex w-full mb-6">
          <FormattedMessage id="nav.appName" />
        </Logo>
        {/* The brand lockup is the visual title; keep the page heading for assistive tech only */}
        <h1 className="sr-only">
          <FormattedMessage id="auth.login" />
        </h1>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <div>
            <label htmlFor="username" className="block text-sm text-foreground mb-1">
              <FormattedMessage id="auth.username" />
            </label>
            <Input id="username" {...register('username')} />
            {errors.username && (
              <p className="text-sm text-destructive">{errors.username.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="password" className="block text-sm text-foreground mb-1">
              <FormattedMessage id="auth.password" />
            </label>
            <Input id="password" type="password" {...register('password')} />
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}
          </div>
          {error && (
            <div className="text-sm text-destructive">
              <FormattedMessage id="auth.loginError" />
            </div>
          )}
          <Button type="submit" className="w-full" disabled={isPending}>
            {isPending ? (
              <FormattedMessage id="auth.loggingIn" />
            ) : (
              <FormattedMessage id="auth.loginButton" />
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
